import { TimesheetState } from '../timesheet-collection.state';

/**
 * How far back the timesheet collection is kept.
 *
 * The window is configured per market by `PreviousDaysTimesheetLimit`, read through
 * `selectPreviousDaysTimesheetLimit`. Without it the collection grows for as long as the app is
 * installed: every submit retains its timesheet and the whole array is persisted under one storage
 * key, so nothing ever leaves.
 *
 * Applied where storage enters the slice rather than at initialisation, because the slice is
 * rehydrated on every completed sync as well as at init — trimming only at init would let storage
 * put the old timesheets straight back.
 */

/**
 * The instant a retention window opens: the start of the local day `days` days before the one `now`
 * falls in.
 *
 * Whole local days rather than an offset from the current instant, because "the previous 30 days" is
 * a statement about days, and an instant-based cutoff would drop a timesheet part-way through the
 * day it became too old. Local rather than UTC for the same reason the bound ceiling in
 * `buildTimesheetRows` is: the day in question is the technician's.
 *
 * The window is inclusive of today, so `days` previous days plus the current one survive — which is
 * what the config's name asks for and errs towards keeping rather than deleting.
 *
 * @param now the current instant as an ISO timestamp
 * @param days how many previous days to keep
 * @returns the cutoff as epoch milliseconds, or undefined when `now` does not parse
 */
const retentionCutoff = (now: string, days: number): number | undefined => {
    const parsed = Date.parse(now);
    if (Number.isNaN(parsed)) {
        return undefined;
    }

    const cutoff = new Date(parsed);
    cutoff.setDate(cutoff.getDate() - days);
    cutoff.setHours(0, 0, 0, 0);
    return cutoff.getTime();
};

/**
 * The timesheets that fall inside the retention window, and the one being worked on whatever its
 * date.
 *
 * Three things are deliberately never dropped:
 *
 * - **The unsubmitted timesheet.** Exactly one timesheet is open at a time and it is the only
 *   editable one; the reducers and selectors are written around it existing. A freshly seeded one
 *   has no start of work at all, so an age test alone would delete it.
 * - **A timesheet whose start of work does not parse.** `Date.parse('')` is `NaN`, and an unset or
 *   malformed instant means "unknown", never "infinitely old". Deleting on the strength of a value
 *   that could not be read is how data goes missing.
 * - **Everything, when no window is configured.** A limit of `0` is what
 *   `selectPreviousDaysTimesheetLimit` falls back to, so it has to mean "keep the lot" — that is
 *   both the behaviour before this existed and the safe reading if the config has not loaded yet.
 *
 * @param timesheets the timesheets to filter, as storage held them
 * @param now the current instant as an ISO timestamp
 * @param days how many previous days to keep; `0` or less keeps everything
 * @returns the timesheets to keep, in the order given
 */
export function withinRetention(timesheets: TimesheetState[], now: string, days: number): TimesheetState[] {
    const cutoff = days > 0 ? retentionCutoff(now, days) : undefined;
    if (cutoff === undefined) {
        return timesheets;
    }

    return timesheets.filter((timesheet) => {
        if (!timesheet.isSubmitted) {
            return true;
        }
        const startOfWork = Date.parse(timesheet.startOfWork);
        return Number.isNaN(startOfWork) || startOfWork >= cutoff;
    });
}
